import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../constants/providers/AuthProvider';
import { supabase } from '../lib/supabase';
import { decodeJWTPayload } from '../utils/jwt';

export interface CurrentCompanyData {
  companyId: string | null;
  role: string | null;
  company: {
    id: string;
    name: string;
    logo_url: string | null;
  } | null;
  allCompanies: Array<{
    company_id: string;
    role: string;
    company: {
      id: string;
      name: string;
      logo_url: string | null;
    };
  }>;
}

/**
 * Hook to get the current user's company ID and role
 * Uses last_selected_company from user_profiles, or falls back to first company relationship
 * Similar to mobile app's useCurrentCompanySelector
 * Returns role for the current company context
 */
export function useCurrentCompany() {
  const { session, loading: authLoading } = useAuth();
  const userId = session?.user?.id ?? null;
  const accessToken = session?.access_token ?? null;

  const jwtClaims = useMemo(() => {
    if (!accessToken) {
      return { company_id: null, user_role: null };
    }
    try {
      const claims = decodeJWTPayload(accessToken);
      return {
        company_id: claims?.company_id || null,
        user_role: claims?.user_role || null,
      };
    } catch {
      return { company_id: null, user_role: null };
    }
  }, [accessToken]);

  const query = useQuery({
    queryKey: ['current-company', { userId, jwtCompanyId: jwtClaims.company_id, jwtRole: jwtClaims.user_role }],
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CurrentCompanyData> => {
      if (!userId) {
        throw new Error('Not authenticated');
      }

      const jwtCompanyId = jwtClaims.company_id || null;
      const jwtRole = jwtClaims.user_role || null;

      const { data: relationshipsData, error: relationshipsError } = await supabase
        .from('user_company_relationships')
        .select(`
          company_id,
          role_id,
          companies (
            id,
            name,
            logo_url
          ),
          system_roles (
            role
          )
        `)
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (relationshipsError) {
        throw relationshipsError;
      }

      if (!relationshipsData || relationshipsData.length === 0) {
        return {
          companyId: null,
          role: null,
          company: null,
          allCompanies: [],
        };
      }

      // Always fetch last_selected_company from the profile — it represents the
      // user's explicit company choice and is more reliable than the JWT company_id,
      // which can be stale if the session wasn't refreshed after switching.
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('last_selected_company')
        .eq('id', userId)
        .single();

      // If the profile query fails for reasons other than "no rows", surface the error.
      // A missing profile row is treated as "no last_selected_company" and falls back
      // to the JWT / relationships.
      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }
      const profileCompanyId = profileData?.last_selected_company ?? null;

      const normalizedRelationships = relationshipsData.map(r => ({
        company_id: r.company_id,
        role: r.system_roles?.role || 'unknown',
        company: {
          id: r.companies?.id || r.company_id,
          name: r.companies?.name || 'Unknown Gym',
          logo_url: r.companies?.logo_url ?? null,
        },
        system_role: r.system_roles?.role || null,
      }));

      // Prefer profile's last_selected_company over JWT company_id — the profile
      // is the user's explicit choice, while the JWT may be stale.
      const selectedCompanyId = profileCompanyId || jwtCompanyId;
      const chosen = selectedCompanyId
        ? normalizedRelationships.find(r => r.company_id === selectedCompanyId) || normalizedRelationships[0]
        : normalizedRelationships[0];

      const allCompanies = normalizedRelationships.map(r => ({
        company_id: r.company_id,
        role: r.role,
        company: r.company,
      }));

      // Use the database role for the chosen company, not the JWT role.
      // The JWT user_role reflects a single company context and may not match
      // the selected company (e.g., student at gym A but owner at gym B).
      const currentRole = chosen.system_role || jwtRole || null;

      return {
        companyId: chosen.company_id,
        role: currentRole,
        company: chosen.company,
        allCompanies,
      };
    },
  });

  return {
    data: query.data?.companyId ?? null,
    role: query.data?.role ?? null,
    company: query.data?.company ?? null,
    allCompanies: query.data?.allCompanies ?? [],
    isLoading: query.isLoading || authLoading,
    error: (query.error as Error | null) ?? null,
  };
}

/**
 * Utility function to switch the user's selected company
 * Updates last_selected_company and refreshes the JWT to get new company_id claim
 */
export async function switchCompany(companyId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    throw new Error('Not authenticated');
  }

  // Update last_selected_company
  const { error: updateError } = await supabase
    .from('user_profiles')
    .update({ last_selected_company: companyId })
    .eq('id', user.id);

  if (updateError) {
    throw updateError;
  }

  // Refresh session to get new JWT with updated company_id claim
  // This ensures RLS policies immediately see the new company context
  const { error: refreshError } = await supabase.auth.refreshSession();
  if (refreshError) {
    console.warn('Failed to refresh session after company switch:', refreshError);
    // Non-fatal - JWT will update on next request
  }
}
